import { useState, useEffect, useCallback } from 'react';
import SEOHead from '../components/common/SEOHead';
import { Link } from 'react-router-dom';
import {
  MagnifyingGlassIcon,
  MapPinIcon,
  CalendarIcon,
  ClockIcon,
  EyeIcon,
  ArrowRightIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { API_ENDPOINTS } from '../config/constants';

interface Post {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  featured_image: string;
  featured_image_alt: string;
  published_at: string;
  reading_time: number;
  view_count: number;
  location_name: string;
  location_country: string;
  category_name: string;
  category_color: string;
  first_name: string;
  last_name: string;
}

interface Category {
  name: string;
  color: string;
  count: number;
}

const BlogPage = () => {
  const [posts, setPosts] = useState<Post[]>([]);
  const [filteredPosts, setFilteredPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [categories, setCategories] = useState<Category[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  const extractCategories = useCallback((posts: Post[]) => {
    const categoryMap = new Map();
    posts.forEach(post => {
      if (post.category_name) {
        const existing = categoryMap.get(post.category_name);
        if (existing) {
          existing.count += 1;
        } else {
          categoryMap.set(post.category_name, {
            name: post.category_name,
            color: post.category_color,
            count: 1,
          });
        }
      }
    });
    setCategories(Array.from(categoryMap.values()));
  }, []);

  const fetchPosts = useCallback(async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.posts}`);
      const data = await response.json();

      if (data.success) {
        setPosts(data.data);
        extractCategories(data.data);
      } else {
        setError('Failed to load posts');
      }
    } catch (err) {
      setError('Error connecting to server');
      console.error('Error fetching posts:', err);
    } finally {
      setLoading(false);
    }
  }, [extractCategories]);

  const filterPosts = useCallback(() => {
    let filtered = posts;

    if (searchTerm) {
      filtered = filtered.filter(
        post =>
          post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          post.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
          post.location_name
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          post.location_country
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          post.category_name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (selectedCategory !== 'All') {
      filtered = filtered.filter(
        post => post.category_name === selectedCategory
      );
    }

    setFilteredPosts(filtered);
  }, [posts, searchTerm, selectedCategory]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  useEffect(() => {
    const delayedFilter = setTimeout(() => {
      setIsSearching(true);
      filterPosts();
      setTimeout(() => setIsSearching(false), 300);
    }, 300);

    return () => clearTimeout(delayedFilter);
  }, [filterPosts]);

  useEffect(() => {
    // Check for category query parameter on component mount
    const urlParams = new URLSearchParams(window.location.search);
    const categoryParam = urlParams.get('category');
    if (categoryParam) {
      setSelectedCategory(categoryParam);
    }
  }, []);

  const handleCategoryFilter = (category: string) => {
    setSelectedCategory(category);
    setSearchTerm(''); // Clear search when filtering by category

    // Update URL to reflect category filter
    const url = new URL(window.location.href);
    if (category !== 'All') {
      url.searchParams.set('category', category);
    } else {
      url.searchParams.delete('category');
    }
    window.history.pushState({}, '', url);
  };

  const handleSearch = () => {
    // Search is handled by useEffect and filterPosts function
    // This function can be used for additional search analytics or actions
    if (searchTerm.trim()) {
      console.log('Searching for:', searchTerm);
      // You could add search analytics here
    }
  };

  if (loading) {
    return (
      <>
        <SEOHead
          title="Travel Blog"
          description="Read our latest travel stories, destination guides, and adventure tips. Explore the world through honest reviews and stunning photography."
          path="/blog"
        />
        <div className="min-h-screen bg-white">
          <div className="container-max section-padding">
            <div className="animate-pulse">
              <div className="h-32 skeleton mb-12"></div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="skeleton h-96"></div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <SEOHead
        title="Travel Blog"
        description="Read our latest travel stories, destination guides, and adventure tips. Explore the world through honest reviews and stunning photography."
        path="/blog"
      />

      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-gray-50 via-white to-blue-50 section-padding-sm">
          <div className="container-max text-center">
            <h1 className="heading-lg text-gray-900 mb-6">
              Travel Stories & Adventures
            </h1>
            <p className="text-large text-gray-600 max-w-3xl mx-auto">
              Discover amazing destinations, practical travel tips, and
              inspiring stories from our adventures around the world. Join us as
              we explore the planet's most beautiful places.
            </p>
          </div>
        </section>

        {/* Search & Filter Section */}
        <section className="bg-white border-b border-gray-100 section-padding-sm">
          <div className="container-max">
            <div className="space-y-6">
              {/* Search - Centered */}
              <div className="flex justify-center">
                <div className="relative w-full max-w-2xl">
                  <MagnifyingGlassIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <input
                    type="text"
                    placeholder="Search destinations, stories, or tips..."
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    onKeyPress={e => e.key === 'Enter' && handleSearch()}
                    className="w-full pl-12 pr-32 py-4 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900 text-base"
                  />

                  {/* Clear Search Button */}
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm('')}
                      className="absolute right-24 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                      aria-label="Clear search"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  )}

                  {/* Search Button */}
                  <button
                    onClick={handleSearch}
                    disabled={isSearching}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-gray-900 text-white px-4 py-2 rounded-xl hover:bg-gray-800 disabled:opacity-50 transition-colors duration-200 flex items-center space-x-1"
                  >
                    {isSearching ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    ) : (
                      <MagnifyingGlassIcon className="h-4 w-4" />
                    )}
                    <span className="text-sm font-medium hidden sm:inline">
                      {isSearching ? 'Searching...' : 'Search'}
                    </span>
                  </button>
                </div>
              </div>

              {/* Category Filter - Centered */}
              <div className="flex justify-center">
                <div className="flex items-center gap-4 overflow-x-auto pb-2">
                  <button
                    onClick={() => handleCategoryFilter('All')}
                    className={`px-6 py-3 rounded-full font-medium transition-colors whitespace-nowrap ${
                      selectedCategory === 'All'
                        ? 'bg-gray-900 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    All ({posts.length})
                  </button>
                  {categories.map(category => (
                    <button
                      key={category.name}
                      onClick={() => handleCategoryFilter(category.name)}
                      className={`px-6 py-3 rounded-full font-medium transition-colors whitespace-nowrap ${
                        selectedCategory === category.name
                          ? 'text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                      style={{
                        backgroundColor:
                          selectedCategory === category.name
                            ? category.color
                            : undefined,
                      }}
                    >
                      {category.name} ({category.count})
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Posts Grid */}
        <section className="section-padding bg-gray-50">
          <div className="container-max">
            {error ? (
              <div className="text-center py-16">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-red-600 text-2xl">!</span>
                </div>
                <p className="text-red-600 text-lg mb-6">{error}</p>
                <button onClick={fetchPosts} className="btn-primary">
                  Try Again
                </button>
              </div>
            ) : filteredPosts.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MagnifyingGlassIcon className="w-6 h-6 text-gray-400" />
                </div>
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-2">
                  No stories found
                </h3>
                <p className="text-gray-600 mb-6">
                  {searchTerm || selectedCategory !== 'All'
                    ? 'Try adjusting your search or filter criteria.'
                    : 'Check back soon for new adventures!'}
                </p>
                {(searchTerm || selectedCategory !== 'All') && (
                  <button
                    onClick={() => {
                      setSearchTerm('');
                      handleCategoryFilter('All');
                    }}
                    className="btn-secondary"
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            ) : (
              <>
                {/* Results Count */}
                <div className="mb-8">
                  <p className="text-gray-600">
                    Showing {filteredPosts.length} of {posts.length} stories
                    {searchTerm && ` for "${searchTerm}"`}
                    {selectedCategory !== 'All' && ` in ${selectedCategory}`}
                  </p>
                </div>

                {/* Posts Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {filteredPosts.map(post => (
                    <Link
                      key={post.id}
                      to={`/blog/${post.slug}`}
                      className="blog-card group"
                    >
                      <article className="card-minimal h-full">
                        {post.featured_image && (
                          <div className="relative h-64 overflow-hidden">
                            <img
                              src={post.featured_image}
                              alt={post.featured_image_alt || post.title}
                              className="blog-card-image"
                            />

                            {/* Category Badge */}
                            {post.category_name && (
                              <div className="absolute top-4 left-4">
                                <span
                                  className="px-3 py-1 rounded-full text-white text-sm font-semibold"
                                  style={{
                                    backgroundColor: post.category_color,
                                  }}
                                >
                                  {post.category_name}
                                </span>
                              </div>
                            )}

                            {/* Reading Time */}
                            <div className="absolute top-4 right-4">
                              <div className="bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-medium text-gray-900">
                                <ClockIcon className="w-4 h-4 inline mr-1" />
                                {post.reading_time} min
                              </div>
                            </div>
                          </div>
                        )}

                        <div className="p-6">
                          <h2 className="text-xl font-heading font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors line-clamp-2">
                            {post.title}
                          </h2>

                          <p className="text-gray-600 mb-4 line-clamp-3">
                            {post.excerpt}
                          </p>

                          <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                            <div className="flex items-center space-x-3">
                              <span className="font-medium">
                                By {post.first_name} {post.last_name}
                              </span>
                              {post.location_name && (
                                <div className="flex items-center">
                                  <MapPinIcon className="w-4 h-4 mr-1" />
                                  <span>{post.location_name}</span>
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4 text-sm text-gray-500">
                              <div className="flex items-center">
                                <CalendarIcon className="w-4 h-4 mr-1" />
                                <span>
                                  {format(
                                    new Date(post.published_at),
                                    'MMM dd, yyyy'
                                  )}
                                </span>
                              </div>
                              <div className="flex items-center">
                                <EyeIcon className="w-4 h-4 mr-1" />
                                <span>{post.view_count}</span>
                              </div>
                            </div>

                            <div className="flex items-center text-blue-600 font-medium group-hover:text-blue-700">
                              <span className="mr-1">Read</span>
                              <ArrowRightIcon className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                            </div>
                          </div>
                        </div>
                      </article>
                    </Link>
                  ))}
                </div>
              </>
            )}
          </div>
        </section>

        {/* Newsletter CTA */}
        <section className="section-padding bg-gray-900 text-white">
          <div className="container-max text-center">
            <h2 className="heading-md mb-6">Get More Travel Inspiration</h2>
            <p className="text-large text-gray-300 mb-8 max-w-2xl mx-auto">
              Join our newsletter for exclusive travel tips, destination guides,
              and stories from the road.
            </p>
            <div className="max-w-md mx-auto">
              <form className="flex flex-col sm:flex-row gap-4">
                <input
                  type="email"
                  placeholder="Your email address"
                  className="flex-1 input-field text-gray-900"
                />
                <button
                  type="submit"
                  className="btn-primary bg-white text-gray-900 hover:bg-gray-100"
                >
                  Subscribe
                </button>
              </form>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default BlogPage;
